import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { VoucherAccountMapping } from "@/lib/db-types";

/** po_account_id -> template_id. Global lærings-cache. */
export function useVoucherAccountMappings() {
  return useQuery({
    queryKey: ["voucher-account-mappings"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<number, string>> => {
      const { data, error } = await supabase
        .from("voucher_account_mappings")
        .select("po_account_id, template_id");
      if (error) throw error;
      const m = new Map<number, string>();
      for (const r of (data ?? []) as VoucherAccountMapping[]) {
        m.set(r.po_account_id, r.template_id);
      }
      return m;
    },
  });
}

export function useUpsertVoucherAccountMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: Array<{ po_account_id: number; template_id: string }>) => {
      if (rows.length === 0) return;
      // VIB-1150: dedupliser på po_account_id. Et bilag splittet på flere
      // budsjettlinjer kan gi flere rader med samme konto; Postgres avviser at
      // ON CONFLICT rører samme rad to ganger i én kommando (500). Map beholder
      // siste oppføring per konto (last-write-wins, samme som upsert ville gjort).
      const deduped = Array.from(
        new Map(rows.map((r) => [r.po_account_id, r])).values(),
      );
      const { error } = await supabase
        .from("voucher_account_mappings")
        .upsert(deduped.map((r) => ({ ...r, updated_at: new Date().toISOString() })), {
          onConflict: "po_account_id",
        });
      if (error) throw error;
    },
    onSettled: () => invalidateFor(qc, "voucher_account_mappings"),
  });
}
